<template>
    <div
        v-if="!loading && !error && domains.length > 0"
        class="all-domains-section"
    >
        <h2><i class="pi pi-cloud"></i> All Domains</h2>
        <div class="domains-grid">
            <Card
                v-for="domain in domains"
                :key="domain.identity"
                class="domain-card"
                @click="navigateToDomain(domain.identity)"
            >
                <template #title>{{ domain.name || domain.identity }}</template>
                <template #content>
                    <div v-if="domain.metrics" class="metrics-preview">
                        <div class="metric">
                            <span class="metric-label">Total Events:</span>
                            <span class="metric-value">{{
                                getMetricValue(domain, "total_events")
                            }}</span>
                        </div>
                        <div class="metric">
                            <span class="metric-label">Error Rate:</span>
                            <span class="metric-value"
                                >{{ getErrorRate(domain) }}%</span
                            >
                        </div>
                    </div>
                    <div v-else class="no-metrics">
                        <span class="metric-label">No metrics available</span>
                    </div>
                </template>
            </Card>
        </div>
    </div>
    <div v-else-if="loading" class="loading">
        <Spinner />
    </div>
    <div v-else-if="error" class="error">
        <Message severity="error">{{ error }}</Message>
    </div>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Message from "primevue/message";
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useClient, useServices } from "@/lib/openapi";
import type { LiveMetrics } from "@/lib/openapi/gen/types.gen";
import Spinner from "@/shared/Spinner.vue";

defineOptions({
    name: "AllDomainsSection",
});

const props = defineProps<{
    hours: number;
    stekRelease?: string | null;
}>();

const route = useRoute();
const router = useRouter();
const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});
const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const domains = ref<
    Array<{ identity: string; name?: string; metrics?: LiveMetrics }>
>([]);
const loading = ref(false);
const error = ref<string | null>(null);

// Helper functions to ensure reactivity
const getMetricValue = (
    domain: { metrics?: LiveMetrics },
    key: keyof LiveMetrics
): number => {
    const value = domain.metrics?.[key];
    if (typeof value === "number") {
        return value;
    }
    return 0;
};

const getErrorRate = (domain: { metrics?: LiveMetrics }): string => {
    const rate = domain.metrics?.error_rate;
    if (typeof rate === "number") {
        return (rate * 100).toFixed(2);
    }
    return "0.00";
};

async function fetchDomains() {
    loading.value = true;
    error.value = null;
    try {
        const domainsResult =
            await api.startDomainDomainMgtGetDomainsByStekStartPost();
        if (domainsResult.response.ok && domainsResult.data) {
            let domainsData: Array<Record<string, unknown>> = [];
            if (Array.isArray(domainsResult.data)) {
                domainsData = domainsResult.data as Array<
                    Record<string, unknown>
                >;
            } else if (
                domainsResult.data &&
                typeof domainsResult.data === "object" &&
                "items" in (domainsResult.data as Record<string, unknown>) &&
                Array.isArray(
                    (domainsResult.data as { items: unknown[] }).items
                )
            ) {
                domainsData = (
                    domainsResult.data as {
                        items: Array<Record<string, unknown>>;
                    }
                ).items;
            }

            // Fetch metrics for each domain with a limit to prevent overwhelming the backend
            // Process in smaller batches to avoid too many concurrent requests
            const batchSize = 5; // Process 5 domains at a time
            const domainsWithMetrics: Array<{
                identity: string;
                name: string;
                metrics?: LiveMetrics;
            }> = [];

            for (let i = 0; i < domainsData.length; i += batchSize) {
                const batch = domainsData.slice(i, i + batchSize);
                const batchResults = await Promise.all(
                    batch.map(async (d: Record<string, unknown>) => {
                        const domainId = String(d.identity || d.id || d);
                        const name = String(d.name || d.title || "");
                        try {
                            const response = await client.post<LiveMetrics>({
                                url: "/stek/live_mgt/get_metrics/start",
                                body: {
                                    domain_id: domainId,
                                    hours: props.hours,
                                    stek_release: props.stekRelease,
                                },
                            });

                            if (!response.response.ok || !response.data) {
                                return {
                                    identity: domainId,
                                    name,
                                };
                            }

                            const metrics = response.data;
                            return {
                                identity: domainId,
                                name,
                                metrics,
                            };
                        } catch {
                            return {
                                identity: domainId,
                                name,
                            };
                        }
                    })
                );
                domainsWithMetrics.push(...batchResults);
            }
            domains.value = domainsWithMetrics;
        }
    } catch (e) {
        error.value = e instanceof Error ? e.message : "Failed to load domains";
    } finally {
        loading.value = false;
    }
}

function navigateToDomain(domainId: string) {
    const orgName = route.params.orgName;
    const stekName = route.params.stekName;
    router.push({
        name: "liveDomainPage",
        params: { orgName, stekName, domainId },
    });
}

onMounted(() => {
    fetchDomains();
});

watch([() => props.hours, () => props.stekRelease], () => {
    fetchDomains();
});
</script>

<style scoped lang="scss">
.all-domains-section {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 2px solid var(--surface-border);

    h2 {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
            font-size: 1.2rem;
        }
    }
}

.domains-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
}

.domain-card {
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.metrics-preview {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.metric {
    display: flex;
    justify-content: space-between;
}

.metric-label {
    font-weight: 500;
}

.metric-value {
    font-weight: bold;
}

.loading,
.error {
    text-align: center;
    padding: 2rem;
}
</style>
